import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "./Reveal";
import { scrollToId } from "../../lib/scroll";

const HERO_IMG =
    "https://images.pexels.com/photos/20649566/pexels-photo-20649566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lineVariants = {
    hidden: { y: "112%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE },
    }),
};

const MaskedLine = ({ index, children, className = "" }) => (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
            custom={index}
            variants={lineVariants}
            initial="hidden"
            animate="show"
            className={`block ${className}`}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 220]);
    const bgScale = useTransform(scrollY, [0, 900], [1.05, 1.18]);
    const fade = useTransform(scrollY, [0, 600], [1, 0]);

    return (
        <section id="top" className="relative min-h-screen flex flex-col overflow-hidden">
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Aerial view of a city seen through clouds"
                    className="w-full h-full object-cover img-treatment"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#050507]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,5,7,0.65)_100%)]" />
            <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[70vw] h-[40vh] bg-[#A855F7]/15 blur-[140px] rounded-full pointer-events-none" />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-5 sm:px-8 pt-32 pb-24"
            >
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.15 }}
                    className="font-mono-meta text-[11px] sm:text-xs tracking-[0.45em] uppercase text-[#A855F7] mb-8"
                    data-testid="hero-eyebrow"
                >
                    An independent game studio
                </motion.p>

                <div
                    className="font-display font-extrabold text-white leading-[0.9] tracking-tight whitespace-nowrap text-[clamp(1.9rem,9vw,7.5rem)]"
                    aria-label="WRLDKIND Studios"
                >
                    <MaskedLine index={0}>
                        WRLD<span className="text-[#A855F7]">KIND</span>
                    </MaskedLine>
                    <MaskedLine index={1} className="text-stroke-ghost tracking-[0.06em]">
                        STUDIOS
                    </MaskedLine>
                </div>

                <h1 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-serif-accent italic text-white/95 leading-tight">
                    <MaskedLine index={2}>Worlds Worth</MaskedLine>
                    <MaskedLine index={3}>Belonging To.</MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.05, ease: EASE }}
                    className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
                    data-testid="hero-intro"
                >
                    We are building the next generation of social online worlds — persistent
                    places where identity, community, and memory matter more than combat.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.25, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-cta-vision"
                        onClick={() => scrollToId("#vision")}
                        className="group inline-flex items-center gap-3 bg-white text-black font-mono-meta text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:bg-[#A855F7] hover:text-white transition-colors duration-300"
                    >
                        Discover the vision
                        <span className="w-1.5 h-1.5 bg-current group-hover:animate-pulse" />
                    </button>
                    <a
                        data-testid="hero-cta-x"
                        href="https://x.com/WRLDKINDStudios"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 border border-white/25 text-white font-mono-meta text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:border-[#A855F7] hover:text-[#A855F7] transition-colors duration-300"
                    >
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        Follow the build
                    </a>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-8 flex items-end justify-between"
            >
                <p className="font-mono-meta text-[10px] tracking-[0.3em] uppercase text-slate-500">
                    40.7128° N / 74.0060° W — Somewhere in America
                </p>
                <div className="flex flex-col items-center gap-2">
                    <span className="font-mono-meta text-[10px] tracking-[0.3em] uppercase text-slate-500">
                        Scroll
                    </span>
                    <span className="block w-px h-12 bg-[#A855F7]/70 animate-scroll-cue" />
                </div>
            </motion.div>
        </section>
    );
};
